import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import UserAvatar from "@/components/UserAvatar";
import StatusBadge from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, UserPlus, Mail, Shield, KeyRound, UserX, UserCheck, Check, X } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  { value: "member", label: "Team Member" },
  { value: "admin", label: "Manager / Admin" },
  { value: "super_admin", label: "Super Admin" },
];

const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r;

const emptyInvite = { name: "", email: "", password: "", role: "member", designation: "", department: "", phone: "", finance_access: false };

export default function Team() {
  const { user: me } = useAuth();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState([]);
  const [pending, setPending] = useState([]);
  const [disabled, setDisabled] = useState([]);
  const [tab, setTab] = useState(params.get("tab") || "active");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState(emptyInvite);
  const [manageUser, setManageUser] = useState(null);
  const [manageForm, setManageForm] = useState(null);
  const [approveUser, setApproveUser] = useState(null);
  const [approveForm, setApproveForm] = useState({ role: "member", finance_access: false });
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const canManage = me?.role === "super_admin" || me?.role === "admin";
  const isSuper = me?.role === "super_admin";

  useEffect(() => { const t = params.get("tab"); if (t && t !== tab) setTab(t); }, [params]);
  const setTabAndUrl = (v) => { setTab(v); const p = new URLSearchParams(params); p.set("tab", v); setParams(p); };

  const load = async () => {
    try {
      const [act, pend, all] = await Promise.all([
        api.get("/team/workload"),
        canManage ? api.get("/team/pending") : Promise.resolve({ data: [] }),
        canManage ? api.get("/users", { params: { status: "disabled" } }) : Promise.resolve({ data: [] }),
      ]);
      setActive(act.data);
      setPending(pend.data);
      setDisabled(all.data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async () => {
    if (!invite.name || !invite.email || invite.password.length < 8) {
      toast.error("Name, email and 8+ char password are required.");
      return;
    }
    try {
      await api.post("/team", invite);
      toast.success("Team member added");
      setInviteOpen(false); setInvite(emptyInvite);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openManage = (u) => {
    setManageUser(u);
    setManageForm({
      name: u.name || "", role: u.role, designation: u.designation || "",
      department: u.department || "", phone: u.phone || "",
      finance_access: !!u.finance_access,
    });
  };

  const saveManage = async () => {
    if (!manageUser) return;
    try {
      await api.put(`/team/${manageUser.id}`, manageForm);
      toast.success("Team member updated");
      setManageUser(null); setManageForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const disableMember = async () => {
    if (!manageUser) return;
    try { await api.post(`/team/${manageUser.id}/disable`); toast.success("Member disabled"); setManageUser(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const enableMember = async (u) => {
    try { await api.post(`/team/${u.id}/enable`); toast.success("Member re-enabled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openApprove = (u) => { setApproveUser(u); setApproveForm({ role: "member", finance_access: false }); };
  const submitApprove = async () => {
    if (!approveUser) return;
    try {
      await api.post(`/team/${approveUser.id}/approve`, approveForm);
      toast.success(`${approveUser.name} approved`);
      setApproveUser(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rejectPending = async (u) => {
    if (!window.confirm(`Reject access request from ${u.name}?`)) return;
    try { await api.post(`/team/${u.id}/reject`); toast.success("Request rejected"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReset = (u) => { setResetUser(u); setResetPw(""); };
  const submitReset = async () => {
    if (!resetUser || resetPw.length < 8) { toast.error("Password must be 8+ chars"); return; }
    try {
      await api.post(`/team/${resetUser.id}/reset-password`, { password: resetPw });
      toast.success("Password reset");
      setResetUser(null); setResetPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">People</div>
          <h1 className="text-4xl font-black tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage members, approve access requests, and assign roles.</p>
        </div>
        {canManage && (
          <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
            <SheetTrigger asChild>
              <Button data-testid="invite-team-btn" className="bg-primary hover:bg-primary/90"><UserPlus size={14} className="mr-1" />Add Team Member</Button>
            </SheetTrigger>
            <SheetContent className="sm:max-w-md overflow-y-auto">
              <SheetHeader><SheetTitle>Add Team Member</SheetTitle></SheetHeader>
              <div className="mt-5 space-y-3">
                <div><Label className="font-mono text-[10px] uppercase">Name *</Label><Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Email *</Label><Input data-testid="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Initial password (min 8) *</Label><Input data-testid="invite-password" type="text" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} placeholder="Share with them securely" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="font-mono text-[10px] uppercase">Role</Label>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v, finance_access: v !== "member" })}>
                      <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.filter((r) => r.value !== "super_admin" || isSuper).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Department</Label><Input value={invite.department} onChange={(e) => setInvite({ ...invite, department: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="font-mono text-[10px] uppercase">Designation</Label><Input value={invite.designation} onChange={(e) => setInvite({ ...invite, designation: e.target.value })} /></div>
                  <div><Label className="font-mono text-[10px] uppercase">Phone</Label><Input value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} /></div>
                </div>
                <div className="flex items-center justify-between border border-border rounded-md p-3">
                  <div>
                    <div className="text-sm font-semibold">Finance access</div>
                    <div className="text-xs text-muted-foreground">Can see quotations, payments and outstanding.</div>
                  </div>
                  <Switch data-testid="invite-finance" checked={invite.finance_access} onCheckedChange={(v) => setInvite({ ...invite, finance_access: v })} />
                </div>
                <Button data-testid="invite-submit" onClick={submitInvite} className="w-full bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />Create Login</Button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTabAndUrl}>
        <TabsList>
          <TabsTrigger data-testid="tab-active" value="active">Active ({active.length})</TabsTrigger>
          {canManage && <TabsTrigger data-testid="tab-pending" value="pending">
            Pending {pending.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-mono font-bold">{pending.length}</span>}
          </TabsTrigger>}
          {canManage && <TabsTrigger data-testid="tab-disabled" value="disabled">Disabled ({disabled.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="active" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {active.map((m) => {
              const loadPct = Math.min(100, (m.open_tasks / 12) * 100);
              return (
                <div key={m.id} className="border border-border rounded-md p-5 bg-card" data-testid={`team-card-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={m.name} size={44} />
                      <div>
                        <div className="font-display font-extrabold text-base leading-tight">{m.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{m.designation || roleLabel(m.role)}</div>
                      </div>
                    </div>
                    <StatusBadge value={m.workload} />
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Shield size={10} />{roleLabel(m.role)}</span>
                    {m.finance_access && <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Finance</span>}
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-1 text-center">
                    <div className="border border-border rounded-md py-2">
                      <div className="font-display font-black text-lg">{m.open_tasks}</div>
                      <div className="font-mono text-[9px] uppercase text-muted-foreground">Open</div>
                    </div>
                    <div className="border border-border rounded-md py-2">
                      <div className="font-display font-black text-lg text-primary">{m.due_today}</div>
                      <div className="font-mono text-[9px] uppercase text-muted-foreground">Today</div>
                    </div>
                    <div className="border border-border rounded-md py-2">
                      <div className={`font-display font-black text-lg ${m.overdue > 0 ? "text-destructive" : ""}`}>{m.overdue}</div>
                      <div className="font-mono text-[9px] uppercase text-muted-foreground">Overdue</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-[10px] font-mono uppercase text-muted-foreground mb-1"><span>Load</span><span>{m.open_tasks}/12</span></div>
                    <Progress value={loadPct} />
                  </div>
                  <div className="mt-3 flex justify-between text-xs text-muted-foreground font-mono">
                    <span>{m.active_clients} clients</span>
                    <span>{m.active_projects} projects</span>
                    <span>{m.completed_this_month} done</span>
                  </div>

                  {canManage && m.id !== me?.id && (
                    <div className="mt-4 pt-3 border-t border-border flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openManage(m)} data-testid={`manage-btn-${m.id}`}>
                        <Shield size={12} className="mr-1" />Manage
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openReset(m)} data-testid={`reset-btn-${m.id}`}>
                        <KeyRound size={12} />
                      </Button>
                    </div>
                  )}
                  {m.id === me?.id && (
                    <div className="mt-4 pt-3 border-t border-border text-[10px] font-mono uppercase text-muted-foreground text-center">This is you</div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {canManage && (
          <TabsContent value="pending" className="mt-5">
            {pending.length === 0 ? (
              <EmptyState icon={Mail} title="No pending requests" description="Self-registrations will appear here for you to approve or reject." />
            ) : (
              <div className="border border-border rounded-md bg-card divide-y divide-border">
                {pending.map((u) => (
                  <div key={u.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-row-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar name={u.name} size={40} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                        <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                          {u.designation ? `${u.designation} · ` : ""}Requested {fmtRelative(u.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => rejectPending(u)} data-testid={`reject-btn-${u.id}`}>
                        <X size={12} className="mr-1" />Reject
                      </Button>
                      <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => openApprove(u)} data-testid={`approve-btn-${u.id}`}>
                        <Check size={12} className="mr-1" />Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {canManage && (
          <TabsContent value="disabled" className="mt-5">
            {disabled.length === 0 ? (
              <EmptyState icon={UserX} title="No disabled accounts" />
            ) : (
              <div className="border border-border rounded-md bg-card divide-y divide-border">
                {disabled.map((u) => (
                  <div key={u.id} className="p-4 flex items-center justify-between gap-3" data-testid={`disabled-row-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar name={u.name} size={40} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                        <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{roleLabel(u.role)}</div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => enableMember(u)} data-testid={`enable-btn-${u.id}`}>
                      <UserCheck size={12} className="mr-1" />Re-enable
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Manage sheet */}
      <Sheet open={!!manageUser} onOpenChange={(v) => !v && setManageUser(null)}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>Manage {manageUser?.name}</SheetTitle></SheetHeader>
          {manageForm && (
            <div className="mt-5 space-y-3">
              <div className="text-xs text-muted-foreground font-mono">{manageUser?.email}</div>
              <div><Label className="font-mono text-[10px] uppercase">Full name</Label><Input data-testid="manage-name" value={manageForm.name} onChange={(e) => setManageForm({ ...manageForm, name: e.target.value })} /></div>
              <div><Label className="font-mono text-[10px] uppercase">Role</Label>
                <Select value={manageForm.role} onValueChange={(v) => setManageForm({ ...manageForm, role: v, finance_access: v !== "member" ? true : manageForm.finance_access })}>
                  <SelectTrigger data-testid="manage-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.filter((r) => r.value !== "super_admin" || isSuper).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Designation</Label><Input value={manageForm.designation} onChange={(e) => setManageForm({ ...manageForm, designation: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Department</Label><Input value={manageForm.department} onChange={(e) => setManageForm({ ...manageForm, department: e.target.value })} /></div>
              </div>
              <div><Label className="font-mono text-[10px] uppercase">Phone</Label><Input value={manageForm.phone} onChange={(e) => setManageForm({ ...manageForm, phone: e.target.value })} /></div>
              <div className="flex items-center justify-between border border-border rounded-md p-3">
                <div>
                  <div className="text-sm font-semibold">Finance access</div>
                  <div className="text-xs text-muted-foreground">See quotations, payments, outstanding.</div>
                </div>
                <Switch data-testid="manage-finance" checked={manageForm.finance_access} onCheckedChange={(v) => setManageForm({ ...manageForm, finance_access: v })} />
              </div>
              <div className="flex gap-2">
                <Button data-testid="manage-save" onClick={saveManage} className="flex-1 bg-primary hover:bg-primary/90">Save changes</Button>
                <Button data-testid="manage-disable" variant="outline" onClick={disableMember} className="text-destructive hover:text-destructive"><UserX size={14} className="mr-1" />Disable</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Approve dialog */}
      <Dialog open={!!approveUser} onOpenChange={(v) => !v && setApproveUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Approve {approveUser?.name}</DialogTitle>
            <div className="text-xs text-muted-foreground font-mono">{approveUser?.email}</div>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="font-mono text-[10px] uppercase">Assign role</Label>
              <Select value={approveForm.role} onValueChange={(v) => setApproveForm({ ...approveForm, role: v, finance_access: v !== "member" })}>
                <SelectTrigger data-testid="approve-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.filter((r) => r.value !== "super_admin" || isSuper).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div>
                <div className="text-sm font-semibold">Finance access</div>
                <div className="text-xs text-muted-foreground">Can they see money-related pages?</div>
              </div>
              <Switch data-testid="approve-finance" checked={approveForm.finance_access} onCheckedChange={(v) => setApproveForm({ ...approveForm, finance_access: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveUser(null)}>Cancel</Button>
            <Button data-testid="approve-submit" onClick={submitApprove} className="bg-primary hover:bg-primary/90"><Check size={14} className="mr-1" />Approve & Grant Access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset password — {resetUser?.name}</DialogTitle>
            <div className="text-xs text-muted-foreground">Share the new password with them securely.</div>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label className="font-mono text-[10px] uppercase">New password (min 8)</Label>
            <Input data-testid="reset-password-input" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Type new password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
            <Button data-testid="reset-password-submit" onClick={submitReset} className="bg-primary hover:bg-primary/90"><KeyRound size={14} className="mr-1" />Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
